/**
 * Cross-window HINT bus for chat-session list changes (issue #2548).
 *
 * A chat created, renamed or deleted in one browser window did not appear in
 * another window's sidebar until that window was focused for 30+ seconds
 * (`useSessionsQuery`'s `staleTime`) or reloaded. This module carries a
 * one-line hint — "something about this session list changed" — to the other
 * windows of the SAME browser, which then invalidate and refetch the
 * authoritative list.
 *
 * Two properties are deliberate, not incidental:
 *
 * 1. **It is a hint, never state.** No session id, no title, no row of any
 *    kind crosses the channel — only the scope (`sessionType` / `projectId`)
 *    needed to decide WHICH list to refetch, plus the originating identity
 *    needed to decide whether to look at the message at all. The server
 *    therefore stays the only source of row data, which makes the
 *    phantom-row class of defect (a locally-inserted row surviving deletion
 *    as an unopenable entry) impossible by construction, and keeps the
 *    payload's exposure surface at "a module name".
 *
 * 2. **The identity guard is the point.** `BroadcastChannel` is scoped to the
 *    browser ORIGIN, not to the user, so two accounts open in one browser —
 *    or a sign-out/sign-in in one tab — share this channel. Every message
 *    carries the originating user's `oid`, and {@link deliver} re-reads each
 *    subscriber's CURRENT identity per message (never a value captured at
 *    subscribe time) and drops anything that does not match. A subscriber
 *    with no resolvable identity receives nothing.
 *
 * Conventions (channel singleton, `onmessage` dispatch, origin-tab
 * suppression, no-op when the API is missing) follow the existing
 * `fact-check-cross-tab.ts` precedent rather than inventing new ones.
 */

export const CHAT_SESSION_CROSS_TAB_CHANNEL_NAME = "chat-session-events"

/** Session-level changes only. Nothing here may ever be emitted per streamed
 *  chunk — the hot path must stay free of broadcasts. */
export const CHAT_SESSION_CROSS_TAB_EVENT_KINDS = ["created", "renamed", "deleted"] as const
export type ChatSessionCrossTabEventKind = (typeof CHAT_SESSION_CROSS_TAB_EVENT_KINDS)[number]

const eventKinds = new Set<string>(CHAT_SESSION_CROSS_TAB_EVENT_KINDS)

/**
 * The wire payload. Everything on it is either routing (`sessionType`,
 * `projectId`), de-duplication (`eventId`, `originTabId`, `at`) or the
 * identity guard (`userId`). There is intentionally no session id and no
 * title — see this module's header.
 */
export type ChatSessionCrossTabEvent = {
  eventId: string
  originTabId: string
  /** `oid` of the user whose window produced this hint. */
  userId: string
  kind: ChatSessionCrossTabEventKind
  sessionType: string
  projectId: string | null
  at: number
}

export type ChatSessionCrossTabSubscription = {
  /**
   * Resolves the CURRENT signed-in user for this subscriber. Called fresh for
   * every message — never captured once at subscribe time — so a tab whose
   * user changed after subscribing (sign-out then sign-in as somebody else)
   * immediately stops accepting the previous user's hints and starts
   * accepting the new user's, with no re-subscription required. Returning
   * `null`/`undefined` (not signed in yet, settings not initialized) drops
   * everything.
   */
  getUserId: () => string | null | undefined
  onEvent: (event: ChatSessionCrossTabEvent) => void
}

/**
 * Session types that `useSessionsQuery` batches into the SINGLE unscoped
 * `["session-history", "open"]` cache entry. A hint from any of them has to
 * reach a listener on `open`, otherwise a chat created in Navigator would
 * never refresh the Explorer sidebar that is actually displaying it. Kept in
 * lockstep with `use-sessions-query.ts`'s own `listSessionsBatch([...])`.
 */
const LEGACY_OPEN_BATCH_SESSION_TYPES = new Set(["open", "navigator", "document-insight"])
const LEGACY_OPEN_BATCH_OWNER = "open"

let channel: BroadcastChannel | null = null
let tabId: string | null = null
const subscriptions = new Set<ChatSessionCrossTabSubscription>()

export function isChatSessionCrossTabSupported(): boolean {
  return typeof BroadcastChannel !== "undefined"
}

/**
 * Per-page-load id used only to suppress a window's own echo. Held in memory
 * rather than `sessionStorage` (which `fact-check-cross-tab.ts` uses to keep
 * a stable id across reloads): no hint outlives a reload, so a fresh id per
 * load is equivalent, and it keeps this module usable in environments with no
 * `sessionStorage` at all.
 *
 * `crypto.randomUUID()` unguarded, matching `fact-check-cross-tab.ts`: it is
 * available in every secure context, which includes `http://localhost` for
 * local dev and HTTPS everywhere else. Only reached once a channel exists,
 * so a hypothetical insecure-context deployment would fail loudly at the
 * first publish rather than sending unattributable hints.
 */
function getTabId(): string {
  tabId ??= crypto.randomUUID()
  return tabId
}

/**
 * Normalizes a project scope to the same shape `queryKeys.sessionHistory`
 * keys on: anything falsy (including `""`) is the unscoped list.
 */
function normalizeProjectId(projectId: string | null | undefined): string | null {
  return projectId ? projectId : null
}

/**
 * Whether `event` should refresh a session list rendered for `scope`.
 *
 * Scoping requirement of issue #2548: a hint must only affect the matching
 * session-list query, never every cached list. "Matching" here has to mean
 * exactly what the LOCAL cache writes this hint stands in for already mean —
 * a stricter rule silently drops the propagation the issue exists to deliver.
 *
 * The project dimension is therefore a wildcard whenever EITHER side is
 * unscoped, because the writes being mirrored are:
 *
 * - `invalidateQueries({ queryKey: ["session-history", type] })` — rename and
 *   both delete mutations. React Query matches query keys by PREFIX, so this
 *   already refetches every `["session-history", type, projectId]` list.
 *   Demanding equality here would mean a rename or delete never reaches
 *   another window's project-scoped sidebar at all.
 * - `setQueriesData({ queryKey: ["session-history", type] })` /
 *   `getQueryCache().findAll({ queryKey: ["session-history", type] })` — the
 *   durable-turn enrichment and sidebar-cover paths, which likewise touch
 *   every list of that type regardless of project.
 *
 * Two DIFFERENT projects are unrelated queries under both rules and stay
 * isolated, and the widening never crosses `sessionType`.
 */
export function matchesChatSessionScope(
  event: Pick<ChatSessionCrossTabEvent, "sessionType" | "projectId">,
  scope: { sessionType: string; projectId?: string | null }
): boolean {
  const eventProjectId = normalizeProjectId(event.projectId)
  const scopeProjectId = normalizeProjectId(scope.projectId)
  const projectMatches =
    eventProjectId === null || scopeProjectId === null || eventProjectId === scopeProjectId
  if (!projectMatches) return false
  if (event.sessionType === scope.sessionType) return true
  // Legacy batching applies only to the unscoped "open" list — a
  // project-scoped list fetches exactly its own session type.
  return (
    scope.sessionType === LEGACY_OPEN_BATCH_OWNER &&
    scopeProjectId === null &&
    LEGACY_OPEN_BATCH_SESSION_TYPES.has(event.sessionType)
  )
}

/**
 * Structural validation of an inbound payload. This channel is shared by
 * every page on the origin — including other versions of this app — so a
 * message is treated as untrusted input and must prove its shape before any
 * listener sees it.
 */
function parseEvent(data: unknown): ChatSessionCrossTabEvent | null {
  if (typeof data !== "object" || data === null) return null
  const candidate = data as Record<string, unknown>
  const { eventId, originTabId, userId, kind, sessionType, projectId, at } = candidate
  if (typeof eventId !== "string" || eventId === "") return null
  if (typeof originTabId !== "string" || originTabId === "") return null
  if (typeof userId !== "string" || userId === "") return null
  if (typeof kind !== "string" || !eventKinds.has(kind)) return null
  if (typeof sessionType !== "string" || sessionType === "") return null
  if (projectId !== null && typeof projectId !== "string") return null
  if (typeof at !== "number") return null
  return {
    eventId,
    originTabId,
    userId,
    kind: kind as ChatSessionCrossTabEventKind,
    sessionType,
    projectId: normalizeProjectId(projectId),
    at,
  }
}

/**
 * The single chokepoint every inbound message passes through. The identity
 * comparison lives HERE — not in any listener — so no subscriber can ever be
 * handed another account's hint, however it is written.
 */
function deliver(data: unknown): void {
  const event = parseEvent(data)
  if (!event) return
  if (event.originTabId === getTabId()) return

  for (const subscription of subscriptions) {
    let currentUserId: string | null | undefined
    try {
      currentUserId = subscription.getUserId()
    } catch {
      // An identity that cannot be resolved is not a match — fail closed.
      continue
    }
    if (typeof currentUserId !== "string" || currentUserId === "") continue
    if (currentUserId !== event.userId) continue
    try {
      subscription.onEvent(event)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error in cross-tab chat-session listener", error)
    }
  }
}

function getChannel(): BroadcastChannel | null {
  if (!isChatSessionCrossTabSupported()) return null
  if (!channel) {
    channel = new BroadcastChannel(CHAT_SESSION_CROSS_TAB_CHANNEL_NAME)
    channel.onmessage = (event: MessageEvent<unknown>) => deliver(event.data)
  }
  return channel
}

/**
 * Broadcast a session-level hint. Returns whether anything was actually put
 * on the wire, so callers (and tests) can distinguish "sent" from "silently
 * skipped".
 *
 * Skipped — with no channel opened at all — when the API is unavailable or
 * when the originating identity cannot be resolved: an unattributable hint
 * could not be safely filtered by any receiver, so it must never be sent.
 */
export function publishChatSessionCrossTabEvent({
  userId,
  kind,
  sessionType,
  projectId,
}: {
  userId: string | null | undefined
  kind: ChatSessionCrossTabEventKind
  sessionType: string
  projectId?: string | null
}): boolean {
  if (typeof userId !== "string" || userId === "") return false
  if (!sessionType) return false
  const ch = getChannel()
  if (!ch) return false

  const event: ChatSessionCrossTabEvent = {
    eventId: crypto.randomUUID(),
    originTabId: getTabId(),
    userId,
    kind,
    sessionType,
    projectId: normalizeProjectId(projectId),
    at: Date.now(),
  }
  ch.postMessage(event)
  return true
}

/**
 * Start receiving same-user hints. Returns an unsubscribe function that is
 * always safe to call, including where `BroadcastChannel` does not exist.
 */
export function subscribeChatSessionCrossTab(
  subscription: ChatSessionCrossTabSubscription
): () => void {
  getChannel()
  subscriptions.add(subscription)
  return () => {
    subscriptions.delete(subscription)
  }
}

/** Test-only: drop the channel, the tab id and every subscription. */
export function resetChatSessionCrossTab(): void {
  channel?.close()
  channel = null
  tabId = null
  subscriptions.clear()
}
